'use client';

/**
 * AstorOnboardingRail Component
 *
 * Collapsible sidebar (right side, 320px) that displays the user's personalized
 * onboarding plan with sections and milestones. Features include:
 *
 *   - Header with title + overall progress bar
 *   - Sections with collapsible milestones
 *   - "Skip for now" collapse action
 *   - Celebration animation on 100% completion (glow variant by default)
 *   - "Generating..." skeleton state while plan loads
 *
 * Reads all state from useAstorContext() — no direct tRPC calls.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, Box, LinearProgress, Skeleton, Typography, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useMemo } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useTerminology } from '../../hooks/useTerminology';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import { allBusinessPortalTours } from '../AstorContext/tour-configs/all-tours';
import { useAstorContext } from '../AstorContext/useAstorContext';
import { OnboardingSection } from './OnboardingSection';

// ============================================================================
// TYPES
// ============================================================================

export interface AstorOnboardingRailProps {
  /** Width of the rail in pixels */
  width?: number;
  /** Celebration animation variant */
  celebrationVariant?: 'glow' | 'confetti' | 'none';
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_WIDTH = 320;
const RAIL_Z_INDEX = 1200;

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Skeleton loading state shown while the plan is being generated.
 */
function GeneratingSkeleton({ width }: { width: number }): React.JSX.Element {
  const { t } = useI18n();
  const theme = useTheme();

  return (
    <Box
      sx={{
        width,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
      data-testid="onboarding-rail-generating"
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1 }}>
        {t('astor.onboarding.generating')}
      </Typography>
      {/* Fake section skeletons */}
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Skeleton
            variant="rounded"
            height={40}
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              borderRadius: 2,
            }}
          />
          <Skeleton
            variant="rounded"
            height={24}
            width="70%"
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: 1,
              ml: 1,
            }}
          />
          <Skeleton
            variant="rounded"
            height={24}
            width="85%"
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: 1,
              ml: 1,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Celebration overlay shown at 100% completion.
 */
function CelebrationOverlay({ variant }: { variant: 'glow' | 'confetti' | 'none' }): React.JSX.Element | null {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();

  if (variant === 'none' || prefersReducedMotion) return null;

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0, 0.6, 0],
        boxShadow: [
          `inset 0 0 30px ${alpha(theme.palette.success.main, 0)}`,
          `inset 0 0 60px ${alpha(theme.palette.success.main, 0.2)}`,
          `inset 0 0 30px ${alpha(theme.palette.success.main, 0)}`,
        ],
      }}
      transition={{
        duration: 3,
        ease: 'easeInOut',
      }}
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        pointerEvents: 'none',
        zIndex: 1,
      }}
      data-testid="onboarding-celebration"
    />
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AstorOnboardingRail({
  width = DEFAULT_WIDTH,
  celebrationVariant = 'glow',
}: AstorOnboardingRailProps): React.JSX.Element | null {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const { term } = useTerminology();

  const { onboarding, overlay, openAstor, dismissOnboarding, startSpotlightTour, navigateTo } = useAstorContext();

  const { plan, completedMilestones, dismissed, completionPercentage, isLoading, isGenerating } = onboarding;

  // Determine which sections are locked (first section with an unlockCondition
  // that references a prior section not yet fully completed is locked)
  const sectionLockState = useMemo(() => {
    if (!plan) return new Map<string, boolean>();
    const lockMap = new Map<string, boolean>();

    for (const section of plan.sections) {
      if (section.unlockCondition) {
        // Check if the referenced section is fully completed
        const conditionSectionId = section.unlockCondition;
        const conditionSection = plan.sections.find((s) => s.id === conditionSectionId);
        if (conditionSection) {
          const allCompleted = conditionSection.milestones.every(
            (m) => completedMilestones.has(m.id) || m.status === 'completed' || m.status === 'skipped',
          );
          lockMap.set(section.id, !allCompleted);
        } else {
          lockMap.set(section.id, false);
        }
      } else {
        lockMap.set(section.id, false);
      }
    }
    return lockMap;
  }, [plan, completedMilestones]);

  const isComplete = completionPercentage >= 100;

  const handleMilestoneAstorAction = useCallback(
    (milestoneId: string) => {
      // Look up the milestone in the plan to check its astorAction type
      const milestone = plan?.sections.flatMap((s) => s.milestones).find((m) => m.id === milestoneId);
      const action = milestone?.astorAction;

      if (action?.type === 'spotlight_tour') {
        // Find the matching tour config and start the spotlight tour
        const tourConfig = allBusinessPortalTours.find((tc) => tc.id === action.payload);
        if (tourConfig) {
          // Resolve terminology for white-label interpolation
          const tourParams = {
            platformName: term('navigation.platformName'),
            loan: term('entities.loan'),
            case: term('entities.case'),
            cases: term('entities.cases'),
            lead: term('entities.lead'),
            leads: term('entities.leads'),
            document: term('entities.document'),
            documents: term('entities.documents'),
          };
          startSpotlightTour(
            tourConfig.id,
            tourConfig.steps.map((step) => ({
              target: step.target,
              title: t(tourConfig.title, tourParams),
              content: t(step.content, tourParams),
              placement: step.placement ?? 'bottom',
              route: step.route,
            })),
            milestoneId,
          );
          return;
        }
      }

      if (action?.type === 'navigate') {
        navigateTo(action.payload);
        return;
      }

      // Default: guided_conversation — open Astor chat with the payload as prefill
      openAstor('widget', action?.payload ?? `Help me with milestone: ${milestoneId}`);
    },
    [plan, openAstor, startSpotlightTour, navigateTo, t, term],
  );

  const handleDismiss = useCallback(() => {
    dismissOnboarding();
  }, [dismissOnboarding]);

  // Don't render if dismissed or still loading initial state
  if (dismissed || isLoading) return null;

  // Don't render if there's no plan and we're not generating one
  if (!plan && !isGenerating) return null;

  // Hide rail while a spotlight tour is active — it overlaps and clutters the tour
  if (overlay.spotlightTour) return null;

  // Auto-hide when all milestones are complete — no need to keep the rail visible
  if (isComplete) return null;

  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  const slideVariants = {
    hidden: { x: width + 16, opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: width + 16, opacity: 0 },
  };

  return (
    <AnimatePresence>
      <Box
        component={prefersReducedMotion ? 'aside' : motion.aside}
        {...(!prefersReducedMotion && {
          variants: slideVariants,
          initial: 'hidden',
          animate: 'visible',
          exit: 'exit',
          transition: { duration: 0.35, ease: [0.215, 0.61, 0.355, 1] },
        })}
        role="complementary"
        aria-label={t('astor.onboarding.title')}
        sx={{
          position: 'fixed',
          top: 80,
          right: 16,
          bottom: 16,
          width,
          zIndex: RAIL_Z_INDEX,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          border: '1px solid',
          borderColor: glass.border,
          bgcolor: glass.background,
          backdropFilter: glass.backdropBlur,
          boxShadow: glass.shadow,
          overflow: 'hidden',
        }}
        data-testid="astor-onboarding-rail"
      >
        {/* Celebration overlay */}
        {isComplete && <CelebrationOverlay variant={celebrationVariant} />}

        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon
                sx={{
                  fontSize: iconSizes.base,
                  color: isComplete ? 'success.main' : 'primary.main',
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isComplete ? t('astor.onboarding.congratulations') : t('astor.onboarding.title')}
              </Typography>
            </Box>

            {/* Close / Dismiss button */}
            <Box
              component="button"
              onClick={handleDismiss}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                border: 'none',
                borderRadius: 1,
                bgcolor: 'transparent',
                color: 'text.secondary',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.text.primary, 0.08),
                  color: 'text.primary',
                },
              }}
              aria-label={t('astor.onboarding.skipForNow')}
              data-testid="onboarding-rail-dismiss"
            >
              <CloseIcon sx={{ fontSize: iconSizes.md }} />
            </Box>
          </Box>

          {/* Progress bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: isComplete ? 'success.main' : 'primary.main',
                  transition: 'transform 0.6s ease, background-color 0.3s ease',
                },
              }}
              data-testid="onboarding-progress-bar"
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: isComplete ? 'success.main' : 'text.secondary',
                whiteSpace: 'nowrap',
                minWidth: 36,
                textAlign: 'right',
              }}
            >
              {Math.round(completionPercentage)}%
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            position: 'relative',
            zIndex: 2,
          }}
        >
          {isGenerating ? (
            <GeneratingSkeleton width={width - 32} />
          ) : plan ? (
            plan.sections
              .sort((a, b) => a.order - b.order)
              .map((section, index) => (
                <OnboardingSection
                  key={section.id}
                  section={section}
                  completedMilestones={completedMilestones}
                  isLocked={sectionLockState.get(section.id) ?? false}
                  onMilestoneAstorAction={handleMilestoneAstorAction}
                  defaultExpanded={index === 0}
                />
              ))
          ) : null}
        </Box>

        {/* Footer: Skip for now */}
        {!isComplete && (
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Box
              component="button"
              onClick={handleDismiss}
              sx={{
                border: 'none',
                bgcolor: 'transparent',
                color: 'text.secondary',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                fontWeight: 500,
                fontFamily: 'inherit',
                minHeight: 44,
                px: 2,
                borderRadius: 1,
                transition: 'color 0.15s ease',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
              data-testid="onboarding-rail-skip"
            >
              {t('astor.onboarding.skipForNow')}
            </Box>
          </Box>
        )}
      </Box>
    </AnimatePresence>
  );
}

AstorOnboardingRail.displayName = 'AstorOnboardingRail';

export default AstorOnboardingRail;
